import { AnyAction, ThunkDispatch } from 'store/store';
import { MiddlewareAPI } from 'redux';
import State from 'store/State';
import { ConsoleMessageWriter, LogLevel } from '@instinet-analytics/core-lib';
import { NotificationActionType } from 'notification/store/actions';
import { showWarningAsync } from 'notification/store/thunks';
import Services from 'store/Services';

const ThrottleMs = 500;

/*
 * Catches middleware errors, notifying the users and logging the error
 */
export function errorHandlerMiddleware(services: Services) {
    const { loggingService } = services;
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) => {
            let lastSentTime = 0;

            return (action: AnyAction) => {
                try {
                    next(action);
                } catch (actionErr) {
                    try {
                        if (suppressErrorHandlingForAction(action)) {
                            return;
                        }

                        const current = new Date().getTime();

                        if (current - lastSentTime < ThrottleMs) {
                            // Don't attempt to log the errors within ThrottleMs of the last one
                            // Without this we could end up with an infinite loop if a middleware exception is caused by the current state
                            return;
                        }

                        lastSentTime = new Date().getTime();

                        middlewareAPI.dispatch(
                            showWarningAsync(
                                'There has been an unexpected error in the application. Please use the "Contact Support" feature to send details to the support team.',
                                'Unhandled application error'
                            )
                        );
                        loggingService.critical(
                            'store|errorHandlerMiddleware',
                            'Unhandled error in redux middleware',
                            { action, err: actionErr }
                        );
                    } catch (loggingErr) {
                        safeLogToConsole('Unable to log unhandled error', {
                            action,
                            err: actionErr,
                            err2: loggingErr,
                        });
                    }
                }
            };
        };
}

function safeLogToConsole(message: string, data: unknown) {
    try {
        const writer = new ConsoleMessageWriter();
        writer.writeMessage(
            LogLevel.Critical,
            'errorHandlerMiddleware',
            message,
            data
        );
    } catch {
        // Swallow this one
    }
}

function suppressErrorHandlingForAction(action: AnyAction) {
    /*
     * Avoid console noise and a potential infinite loop if the error is due to an action that this middleware dispatches
     */
    return (
        action.type === NotificationActionType.SHOW_NOTIFICATION ||
        action.type === NotificationActionType.HIDE_NOTIFICATION
    );
}
